import { LitElement, paths, html, unsafeHTML, css, utils, t, styles } from '@hh/global.js'
const { externalVideoEmbedSrc } = utils

class AdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    description: {
      type: String,
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    posterImage: {
      type: String,
      attribute: 'poster-image',
    },
    minimalControls: {
      type: Boolean,
      attribute: 'minimal-controls'
    },
    playing: {
      type: Boolean,
      state: true,
      reflect: true
    },
    isCenterButton: {
      type: Boolean,
      attribute: 'center-button',
    },
    isLargeButton: {
      type: Boolean,
      attribute: 'large-button',
    },
    buttonMode: {
      type: String,
      attribute: 'button-mode',
    },
    visibleOn: {
      type: String,
      attribute: 'visible-on',
    },
    isMobile: {
      type: Boolean,
      state: true
    }
  }

  get computedAspectRatio() {
    return (
      eval(this.aspectRatio) ||
      this.videoObject?.aspect_ratio ||
      (this.aspectRatio === 'wide' && 16 / 9) ||
      (this.aspectRatio === 'full' && 4 / 3) ||
      16 / 9
    )
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay
    this.style.setProperty('--hh-aspect-ratio', this.computedAspectRatio)

    this._mobileQuery = window.matchMedia('(max-width: 749px)');
    this._handleViewportChange = this._handleViewportChange.bind(this);
    this._mobileQuery.addEventListener('change', this._handleViewportChange);
    this._handleViewportChange(this._mobileQuery);
  }

  disconnectedCallback() {
    this._mobileQuery.removeEventListener('change', this._handleViewportChange);
  }

  firstUpdated() {
    if (this.host === 'vimeo') {
      this.videoElement = new Vimeo.Player(this.videoElement)
    }
  }

  playYouTubeVideo() {
    if (!this.videoElement?.contentWindow) return

    this.videoElement.contentWindow.postMessage(
      '{"event":"command","func":"playVideo","args":""}',
      '*'
    )
  }

  pauseYouTubeVideo() {
    if (!this.videoElement?.contentWindow) return

    this.videoElement.contentWindow.postMessage(
      '{"event":"command","func":"pauseVideo","args":""}',
      '*'
    )
  }

  handlePlayClick() {
    if (this.playing == false || this.playing == undefined) {
      this.playing = true
      let overlay = this.renderRoot?.querySelector('.video-overlay')
      if (overlay) overlay.style.display = 'none'
      if (this.host === 'youtube') {
        this.playYouTubeVideo()
      } else {
        this.videoElement?.play()
      }
    } else {
      this.playing = false;
      if (this.host === 'youtube') {
        this.pauseYouTubeVideo()
      } else {
        this.videoElement?.pause()
      }
    }
  }

  _handleViewportChange(e) {
    this.isMobile = e.matches;
  }

  generatePosterWithText() {
    if (!this.posterImage) return html``
    return html`
      <div class="video-overlay">
        ${this.title ? html`<h2>${this.title}</h2>` : html``}
        <button
          class="video-poster-button ${this.isCenterButton ? 'video-poster-button__center' : ''} ${this.isLargeButton ? 'video-poster-button__large' : ''}"
          @click="${this.handlePlayClick}"
          aria-label="${t('accessibility.play', 'Play')}"
        >
          ${unsafeHTML(paths.play || html`►`)}
        </button>
        <img src="${this.posterImage}" role="presentation" alt="" />
      </div>
    `
  }

  renderVideo() {
    return html`
      <video
        id="video-element"
        playsinline="playsinline"
        ?autoplay="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        ${this.posterImage ? `poster="${this.posterImage}"` : html``}
        preload="none"
        muted="muted"
        aria-label="${this.title}"
      >
        ${this.videoObject.sources
        .sort((a, b) => b.width - a.width)
        .map(
          (source) =>
            html`<source src="${source.url}" type="${source.mime_type}"></source>`
        )}
        ${this.description != '' ? html`<p>${this.description}</p>` : html``}
      </video>
      ${this.description ? html`<p class="visually-hidden">${this.description}</p>` : html``}
      ${this.minimalControls ? html`
        <button class="video-poster-button video-poster-button-minimal"
          @click="${this.handlePlayClick}"
          aria-label="${this.playing ? `${t('accessibility.pause', 'Pause')}` : `${t('accessibility.play', 'Play')}`}">
          ${this.playing ? html`${unsafeHTML(paths.pause)}` : html`${unsafeHTML(paths.play)}`}
        </button>  
      ` : ``}
    `
  }

  renderIframe() {
    return html`
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${externalVideoEmbedSrc(
      this.host,
      this.id,
      this.autoplay,
      this.controls,
      this.loop
    )}"
        title="${this.title}"
      >
      </iframe>
    `
  }

  render() {
    const visibility = this.visibleOn || 'all'
    const shouldRender =
      visibility === 'all' ||
      (visibility === 'mobile' && this.isMobile) ||
      (visibility === 'desktop' && !this.isMobile)

    if (!shouldRender) return html``

    return html`
      ${['vimeo', 'youtube'].includes(this.host)
        ? this.renderIframe()
        : this.renderVideo()}
      ${this.autoplay ? html`` : this.generatePosterWithText()}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
    :host {
      aspect-ratio: var(--hh-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }
    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .video-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .video-overlay img {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      filter: brightness(var(--hh-overlay-brightness, 0.5));
    }
    .video-poster-button {
      align-items: center;
      aspect-ratio: 1;
      background: rgba(0, 0, 0, 0.2);
      border: none;
      border-radius: 100%;
      bottom: var(--hh-space-sm);
      cursor: pointer;
      display: flex;
      justify-content: center;
      padding: 0;
      position: absolute;
      right: var(--hh-space-sm);
      transition: opacity var(--hh-duration-default);
      width: max(34px, 2vw);
      z-index: 1;
    }

    :host([host='youtube']) .video-poster-button,
    :host([host='vimeo']) .video-poster-button {
      width: max(40px, 2vw);
    }

    :host([button-mode='light']) .video-poster-button {
      background: var(--hh-color-reverse);
    }

    @media screen and (min-width: 750px) {
      .video-poster-button {
        opacity: 0;
      }

      .video-poster-button-minimal {
        opacity: 1;
      }

      .video-poster-button:hover,
      .video-overlay .video-poster-button {
        opacity: 1;
        z-index: 1;
      }

      #video-element:hover + .video-poster-button {
        opacity: 1;
      }
    }

    .video-poster-button__center {
      bottom: 50%;
      right: 50%;
      transform: translate(50%, 50%);
    }

    .video-poster-button__large {
      height: 75px;
      width: 75px;
    }

    .video-poster-button svg {
      color: var(--hh-color-reverse);
      fill: var(--hh-color-reverse);
      width: 35%;
    }

    :host([button-mode='light']) .video-poster-button svg {
      color: var(--hh-color-ink);
      fill: var(--hh-color-ink);
    }

    .video-poster-button__large svg {
      fill: none;
    }

    .video-poster-button__large:hover svg {
      fill: var(--hh-color-reverse);
    }

    :host([button-mode='light']) .video-poster-button__large:hover svg {
      fill: var(--hh-color-ink);
    }

    .video-poster-button__large svg path {
      stroke-width: 1.5;
    }

    img {
      object-fit: var(--hh-object-fit, cover);
    }
  `]
}

window.customElements.define('hh-adaptive-video', AdaptiveVideo)
